import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Copies the current URL (with its live hash) to the clipboard and shows a
 * brief confirmation toast. The hash is kept current by
 * `useUrlSync`, so `location.href` is always the shareable state.
 */
export function ShareButton() {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const onClick = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (insecure context / permissions) — no toast.
    }
  }, []);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onClick}
        className="min-h-11 rounded-lg border border-border bg-panel-2 px-3.5 text-[13px] font-medium text-ink hover:border-accent focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 md:min-h-0 md:py-1.5"
      >
        Copy share link
      </button>
      <span
        role="status"
        aria-live="polite"
        className={`absolute right-0 top-full mt-1 rounded-md bg-accent px-2 py-1 text-xs font-medium text-canvas transition-opacity ${
          copied ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        Link copied
      </span>
    </div>
  );
}
